import type { Vec2, FocusAimState } from '../types.js';

export interface GamepadSample {
  move: Vec2;
  moveMagnitude: number;
  focusAim: FocusAimState;
  dashPressed: boolean;
  pausePressed: boolean;
  firePressed: boolean;
  connected: boolean;
}

const DEADZONE_INNER = 0.18;
const DEADZONE_OUTER = 0.92;
const FOCUS_ENGAGE = 0.4;
const FOCUS_RELEASE = 0.3;

function normalizeOrZero(x: number, y: number): Vec2 {
  const len = Math.hypot(x, y);
  if (len < 1e-8) return { x: 0, y: 0 };
  return { x: x / len, y: y / len };
}

function applyDeadZone(x: number, y: number): { dir: Vec2; magnitude: number } {
  const magnitude = Math.hypot(x, y);
  if (magnitude < DEADZONE_INNER) {
    return { dir: { x: 0, y: 0 }, magnitude: 0 };
  }
  const remapped = Math.min(
    1,
    Math.max(0, (magnitude - DEADZONE_INNER) / (DEADZONE_OUTER - DEADZONE_INNER))
  );
  const inv = 1 / magnitude;
  return {
    dir: { x: x * inv, y: y * inv },
    magnitude: remapped,
  };
}

/**
 * Standard-mapping gamepad adapter.
 *
 * - Left stick moves the player.
 * - Right stick engages Focus Aim (>= 0.40 deflection) and releases (< 0.30).
 * - RB (alt: A / bottom button) dashes on rising edge.
 * - RT (button 7, alt: A) fires the manual weapon on rising edge.
 * - Start/Menu (button 9) pauses on rising edge.
 *
 * Dead zones: inner 0.18, outer 0.92, with magnitude renormalized over the
 * active range.
 */
export class GamepadAdapter {
  private index: number | null = null;
  private prevDash = false;
  private prevPause = false;
  private prevFire = false;
  private focusHeld = false;

  constructor() {
    window.addEventListener('gamepadconnected', this.onConnect);
    window.addEventListener('gamepaddisconnected', this.onDisconnect);
  }

  private onConnect = (e: GamepadEvent): void => {
    this.index = e.gamepad.index;
  };

  private onDisconnect = (e: GamepadEvent): void => {
    if (this.index === e.gamepad.index) {
      this.index = null;
      this.prevDash = false;
      this.prevPause = false;
      this.prevFire = false;
      this.focusHeld = false;
    }
  };

  sample(_tick: number): GamepadSample {
    const gp =
      this.index !== null ? navigator.getGamepads()[this.index] : null;

    if (!gp) {
      return {
        move: { x: 0, y: 0 },
        moveMagnitude: 0,
        focusAim: { held: false, dir: { x: 0, y: 1 } },
        dashPressed: false,
        pausePressed: false,
        firePressed: false,
        connected: false,
      };
    }

    // Left stick: browser y-axis is down; world y is up.
    const leftX = gp.axes[0] ?? 0;
    const leftY = -(gp.axes[1] ?? 0);
    const left = applyDeadZone(leftX, leftY);

    // Right stick.
    const rightX = gp.axes[2] ?? 0;
    const rightY = -(gp.axes[3] ?? 0);
    const rightRawMag = Math.hypot(rightX, rightY);
    const right = applyDeadZone(rightX, rightY);

    // Focus Aim hysteresis uses raw deflection before dead-zone remapping.
    if (rightRawMag >= FOCUS_ENGAGE) {
      this.focusHeld = true;
    } else if (rightRawMag < FOCUS_RELEASE) {
      this.focusHeld = false;
    }

    const focusDir =
      right.magnitude > 0 ? right.dir : { x: 0, y: 1 };

    // Standard mapping: RB = button 5, A = button 0, RT = button 7,
    // Start = button 9.
    const rb = !!gp.buttons[5]?.pressed;
    const a = !!gp.buttons[0]?.pressed;
    const rt = !!gp.buttons[7]?.pressed;
    const start = !!gp.buttons[9]?.pressed;

    const dashHeld = rb || a;
    const pauseHeld = start;
    const fireHeld = rt || a;

    const dashPressed = dashHeld && !this.prevDash;
    const pausePressed = pauseHeld && !this.prevPause;
    const firePressed = fireHeld && !this.prevFire;

    this.prevDash = dashHeld;
    this.prevPause = pauseHeld;
    this.prevFire = fireHeld;

    return {
      move: left.dir,
      moveMagnitude: left.magnitude,
      focusAim: {
        held: this.focusHeld,
        dir: focusDir,
      },
      dashPressed,
      pausePressed,
      firePressed,
      connected: true,
    };
  }

  dispose(): void {
    window.removeEventListener('gamepadconnected', this.onConnect);
    window.removeEventListener('gamepaddisconnected', this.onDisconnect);
  }
}
